import { makeStyles } from '@material-ui/core/styles'
import {
  listComputeServiceDetails,
  listImageServiceDetails,
  listNetworkServiceDetails,
  listStorageServiceDetails,
} from 'app/plugins/infrastructure/components/common/service-health/action'
import {
  aggregateServiceStateOnServiceType,
  getStatus,
  serviceStateCountHelper,
  validServiceDetails,
} from 'app/plugins/infrastructure/components/common/service-health/helper'
import {
  computeServicesSelector,
  imageServicesSelector,
  networkServicesSelector,
  storageServicesSelector,
} from 'app/plugins/infrastructure/components/common/service-health/selector'
import { useControlPlaneServicesCheck } from 'app/plugins/infrastructure/components/common/service-health/ControlPlaneServicesContext'
import { IHealthServiceDetailsPageTabs } from 'app/plugins/openstack/components/networks/model'
import { useAppSelector } from 'app/store'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import PollingData, { POLLING_INTERVALS } from 'core/components/PollingData'
import SimpleLink from 'core/components/SimpleLink'
import Tooltip from 'core/elements/tooltip'
import useListAction from 'core/hooks/useListAction'
import useScrollPosition from 'core/hooks/useScrollPosition'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import React, { useCallback, useEffect, useMemo } from 'react'

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  container: {
    marginRight: `${theme.spacing(-1)}px`,
  },
  success: { color: `${colors.green[80]} !important` },
  warning: { color: `${colors.yellow[500]} !important` },
  error: { color: `${colors.pink[800]} !important` },
  iconWrapper: {
    fontSize: 20,
    background: 'none',
  },
  logo: {
    cursor: 'pointer',
    width: 36,
    height: 36,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    overflow: 'hidden',

    '&:hover': {
      borderRadius: '50%',
      backgroundColor: theme.components.iconButton.background,
      color: theme.components.iconButton.color,
      transition: 'all .2s ease',
    },
  },
}))

const ServiceHealth = () => {
  const scrollY = useScrollPosition()

  const offset = useMemo(() => {
    return { vertical: 12 + scrollY, horizontal: 0 }
  }, [scrollY])

  const classes = useStyles()
  const { reload: reloadComputeService, loading: computeLoading } = useListAction(
    listComputeServiceDetails,
  )
  const { reload: reloadStorageService, loading: storageLoading } = useListAction(
    listStorageServiceDetails,
  )
  const { reload: reloadNetworkService, loading: networkLoading } = useListAction(
    listNetworkServiceDetails,
  )
  const { reload: reloadImageService, loading: imageLoading } = useListAction(
    listImageServiceDetails,
  )

  const computeServiceDetails = validServiceDetails(useAppSelector(computeServicesSelector))
  const storageServiceDetails = validServiceDetails(useAppSelector(storageServicesSelector))
  const networkServiceDetails = validServiceDetails(useAppSelector(networkServicesSelector))
  const imageServiceDetails = validServiceDetails(useAppSelector(imageServicesSelector))

  const computeServiceStates = useMemo(() => serviceStateCountHelper(computeServiceDetails), [
    computeServiceDetails,
  ])
  const storageServiceStates = useMemo(() => serviceStateCountHelper(storageServiceDetails), [
    storageServiceDetails,
  ])
  const networkServiceStates = useMemo(() => serviceStateCountHelper(networkServiceDetails), [
    networkServiceDetails,
  ])
  const imageServiceStates = useMemo(() => serviceStateCountHelper(imageServiceDetails), [
    imageServiceDetails,
  ])

  const {
    serviceHealthStates: controlPlaneServiceStates,
    isLoading: controlPlaneServicesLoading,
    reload: reloadControlPlaneServices,
  } = useControlPlaneServicesCheck()

  const computeServiceState = getStatus(computeServiceStates, true)
  const storageServiceState = getStatus(storageServiceStates)
  const networkServiceState = getStatus(networkServiceStates)
  const imageServiceState = getStatus(imageServiceStates)
  const controlPlaneServiceState = getStatus(controlPlaneServiceStates)

  const aggregatedStates = aggregateServiceStateOnServiceType([
    computeServiceState,
    storageServiceState,
    networkServiceState,
    imageServiceState,
    controlPlaneServiceState,
  ])

  const iconColor =
    aggregatedStates?.unhealthy > 0
      ? classes.error
      : aggregatedStates?.warning > 0
      ? classes.warning
      : aggregatedStates?.healthy > 0
      ? classes.success
      : 'none'

  const handleServiceReloads = useCallback(async (refetch = false, updateLoadingState = true) => {
    reloadComputeService(refetch, updateLoadingState)
    reloadStorageService(refetch, updateLoadingState)
    reloadNetworkService(refetch, updateLoadingState)
    reloadImageService(refetch, updateLoadingState)
    reloadControlPlaneServices(true)
  }, [])

  useEffect(() => {
    handleServiceReloads(true, false)
  }, [])

  const loading =
    computeLoading ||
    storageLoading ||
    networkLoading ||
    imageLoading ||
    controlPlaneServicesLoading

  return (
    <div className={classes.container}>
      <PollingData
        loading={loading}
        onReload={handleServiceReloads}
        refreshDuration={POLLING_INTERVALS.FIVE_MINUTES}
        hidden
      />
      <Tooltip message="Service Health" align={{ vertical: 'bottom', horizontal: 'middle' }}>
        <SimpleLink
          src={routeManager.routes.openstack.health.details.path({
            tab: IHealthServiceDetailsPageTabs.ServiceHealth,
          })}
          textDecoration="none"
        >
          <div className={classes.logo}>
            <FontAwesomeIcon className={clsx(classes.iconWrapper, iconColor)}>
              heart-pulse
            </FontAwesomeIcon>
          </div>
        </SimpleLink>
      </Tooltip>
    </div>
  )
}

export default ServiceHealth
